/**
 * Controller for feeds for Farm2.
 * Does the job of polling and feed appropriate data into the in memory cache.
 * Also stores a copy of the feed in the LS for short term - discards it if required.
 * This controller updates the state of feeds in the registering component.
 */
import DataStore from '../../../../../../data/DataStore';
import ServiceLocator from '../../../../../../ServiceLocator';
import GenericUtils from '../../../../../../util/GenericUtils';
import LocUtils from '../../../../../../util/localization/LocUtils';
import ZLogger from '../../../../../../util/zlogger/zlogger';
import Farm2FeedAPI from './Farm2FeedAPI';
import FeedClaimHelper from './FeedClaimHelper'

/**
 * A set of constants and templates being followed here.
 * Change parameters here to tweak the behaviour.
 */
var FeedConstants = (function () {
    return {
        FEED_POLL_FETCH_FRIEND_MAX_FEEDS: 18,
        FEED_POLL_FETCH_PUBLIC_MAX_FEEDS: 18,
        FEED_POLL_TIMER_SECONDS: 180,
        FEED_MIN_HARD_REFRESH_INTERVAL: 20,
        LOCAL_STORAGE_EXPIRE_INTERVAL_SECONDS: 3600,
        LOCAL_STORAGE_FEED_TEMPLATE: {
            csrf: "",
            feeds: {
                public: [],
                friends: []
            },
            users_profile_public: {},
            users_profile_friends: {},
            lastTS: 0,
            needsRefresh: true
        },
        FEED_IN_MEMORY_TEMPLATE: {
            unique: {
                public: {},
                friends: {}
            },
            user_accountibility: {
                public: {},
                friends: {}
            },
            localStorage: {},
            shown: {
                public: [],
                friends: []
            },
            zFriends: {}
        },
        TRICKLE_FEED_INTERVAL_SECONDS: 7,
        FEED_SHOWN_LIST_MAX_LEN: 20,
        FIRST_TIME_SHOW_MAX: 10,
        FIRST_TIME_SHOW_MAX_FEED: 5,
    }
})();

/**
 * Wrapper to sync the feeds to the LS.
 */
var Farm2FeedLocalStorage = (function () {
    var CACHE_KEY = "FARM2_FEED_DATA";

    function Has(key = "") {
        return DataStore.LocalStorage.Has(CACHE_KEY + "_" + key);
    }

    function Get(key = "") {
        return JSON.parse(DataStore.LocalStorage.Get(CACHE_KEY + "_" + key));
    }

    function Set({
        data = FeedConstants.LOCAL_STORAGE_FEED_TEMPLATE,
        key = ""
    }) {
        if (data) {
            DataStore.LocalStorage.Store(CACHE_KEY + "_" + key, JSON.stringify(data));
        }
    }

    return {
        Get: Get,
        Has: Has,
        Set: Set
    }
})();

/**
 * The actual controller for feed polling and syncing.
 */
var Farm2FeedController = (function () {

    var _zid = undefined;                   /* The zid associated with the logged in user */
    var _csrf = undefined;                  /* The csrf associated with the session */
    var _gameKey = "";                       /* The game key (name) to look for (unique string) */
    var _toggleFriends = false;             /* Wether to show only friend feeds or not */
    var _that = undefined;                  /* Reference of whoever initializes this controller */
    var _locale = undefined;                /* The current active locale, registered once per load */
    var FeedStorageData = {};               /* An in memory data containing the complete feed config */
    var _lastFriendFeedCheckedTs = -1;      /* What was the last time we checked the friend feed? (API rule) */

    var intervalHandlers = {                /* Interval handlers when setting up handlers */
        check: undefined,
        trickle: undefined
    }

    var _filterStrings = [];
    var _isExclusionSearch = false;
    var _lastHardRefreshTime = -1;
    var _lastNormalRefreshTime = -1;
    var _filterFriendsViralPresent= true;
    var _filterPublicViralPresent = true;

    function CanShowFriends() {
        return _toggleFriends;
    }

    function FlipToggleFriends(val) {
        if (!val) val = false;
        _toggleFriends = val;
        _sync();
        // _that.forceUpdate();
    }

    /* Prime the backend for friend feeds 
    function _primeFeeds({ csrf, locale = "en_US", zid }) {
        var feedParams = {
            csrf: csrf,
            locale: locale,
            game: 118,
            max: FeedConstants.FEED_POLL_FETCH_FRIEND_MAX_FEEDS,
            fmt: "zdc",
            alias: "farmville-two",
            "sources[]": zid,
            "prime": true
        }

        var feedParamsPayload = {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            referrer: 'client',
            body: ServiceLocator.NetworkManager.Utils.GetURLEncoded(feedParams)
        }
        ServiceLocator.NetworkManager.Fetch.Call({
            url: process.env.REACT_APP_AUTH_ISSUETOKEN + "/ajax/common_web:feed_neighborGet",
            payload: feedParamsPayload,
            token: "FARM2_FEED_CHECK"
        })
            .then(resp => { return resp.json() })
            .then(resp => {
                ZLogger.log("Priming response: ", resp);
                if (resp && resp.e !== undefined && resp.e === 0) {
                    ZLogger.log("Feeds seem to primed for friends in zdc...");
                } else {
                    ZLogger.fatal("Seems like that the priming encountered an error: ", resp);
                }
            })
            .catch(err => { ZLogger.fatal("Found error when priming friends feed: ", err) })
    }*/

    function _sync() {
        // Flush it back to the component
        _that.setState({
            trickleCompleteList: {
                friends: FeedStorageData.shown.friends,
                everybody: FeedStorageData.shown.public
            },
            showFilterViralNotPresentMsg: (_that.state.fromFriends ? !_filterFriendsViralPresent : !_filterPublicViralPresent)
        })
        // ZLogger.log("Syncing with component complete");
    }

    /** 
     * Run any code block that might be required before polling checks
     * can start running (eg. priming etc)
     
    function InitializeChecks({ locale = _locale }) {
        // In case of friends we will need to prime the feeds first
        _primeFeeds({
            csrf: _csrf,
            locale: locale,
            zid: _zid
        })
    }*/

    /**
     * Trickles feed yet to show feed list
     */
    function Trickle(firstTime = false) {
        if (!!firstTime) {
            let shifted = 0
            // First time fetch 5 feeds at one go 
            while (FeedStorageData.localStorage.feeds.public.length > 0 && shifted < FeedConstants.FIRST_TIME_SHOW_MAX_FEED) {
                let public_feed_extract = FeedStorageData.localStorage.feeds.public.shift();
                if (public_feed_extract) {
                    FeedStorageData.shown.public.unshift(public_feed_extract);
                }
                shifted++;
            }

            shifted = 0
            while (FeedStorageData.localStorage.feeds.friends.length > 0 && shifted < FeedConstants.FIRST_TIME_SHOW_MAX) {
                let friend_feed_extract = FeedStorageData.localStorage.feeds.friends.shift();
                if (friend_feed_extract) {
                    FeedStorageData.shown.friends.unshift(friend_feed_extract);
                }
                shifted++;
            }
            _sync();
            // sync back on LS
            Farm2FeedLocalStorage.Set({
                data: FeedStorageData.localStorage,
                key: _gameKey
            })
            return;
        }

        const publicFeeds = FeedStorageData.localStorage.feeds.public;
        const friendFeeds = FeedStorageData.localStorage.feeds.friends;

        let public_feed = undefined;
        if (_filterStrings.length > 0) {
            let matchingIndex = -1;
            if (_isExclusionSearch) {
                /**
                 * In this search we are filtering the viral which does not have
                 * all the keyword present in the filterString array
                 * @type {number}
                 */
                matchingIndex = publicFeeds.findIndex(feed =>
                    Object.values(feed).some(value =>
                        typeof value === 'string' &&
                        !_filterStrings.every(filter =>
                            value.toLowerCase().includes(filter.toLowerCase())
                        )
                    )
                );
            } else {
                /**
                 * In this search we are filtering the viral which does have atleast one
                 * keyword in the filterStirng array as the substring
                 * @type {number}
                 */
                matchingIndex = publicFeeds.findIndex(feed =>
                    Object.values(feed).some(value =>
                        typeof value === 'string' &&
                        _filterStrings.some(filter =>
                            value.toLowerCase().includes(filter.toLowerCase())
                        )
                    )
                );
            }
            if (matchingIndex >= 0) {
                _filterPublicViralPresent = true;
                public_feed = publicFeeds.splice(matchingIndex, 1)[0];
            } else {
                public_feed = undefined;
                let currentTime = GenericUtils.TimeNowSeconds();
                let timeSinceLastHardRefresh = (_lastHardRefreshTime !== -1) ? (currentTime - _lastHardRefreshTime) : FeedConstants.FEED_MIN_HARD_REFRESH_INTERVAL;
                let timeLeftForNextNormalRefresh = FeedConstants.FEED_POLL_TIMER_SECONDS - (currentTime - _lastNormalRefreshTime);
                if (timeSinceLastHardRefresh >= FeedConstants.FEED_MIN_HARD_REFRESH_INTERVAL && timeLeftForNextNormalRefresh >= FeedConstants.FEED_MIN_HARD_REFRESH_INTERVAL) {
                    Farm2FeedAPI.Check(_lastFriendFeedCheckedTs);
                    _lastHardRefreshTime = GenericUtils.TimeNowSeconds();
                } else {
                    _filterPublicViralPresent = false;
                    public_feed = FeedStorageData.localStorage.feeds.public.shift();
                }

            }
        } else {
            _filterPublicViralPresent = true;
            public_feed = FeedStorageData.localStorage.feeds.public.shift();
        }

        let friend_feed = undefined;
        if (_filterStrings.length > 0) {
            let matchingIndex = -1;
            if (_isExclusionSearch) {
                /**
                 * In this search we are filtering the viral which does not have
                 * all the keyword present in the filterString array
                 * @type {number}
                 */
                matchingIndex = friendFeeds.findIndex(feed =>
                    Object.values(feed).some(value =>
                        typeof value === 'string' &&
                        !_filterStrings.every(filter =>
                            value.toLowerCase().includes(filter.toLowerCase())
                        )
                    )
                );
            } else {
                /**
                 * In this search we are filtering the viral which does have atleast one
                 * keyword in the filterStirng array as the substring
                 * @type {number}
                 */
                matchingIndex = friendFeeds.findIndex(feed =>
                    Object.values(feed).some(value =>
                        typeof value === 'string' &&
                        _filterStrings.some(filter =>
                            value.toLowerCase().includes(filter.toLowerCase())
                        )
                    )
                );
            }
            _filterFriendsViralPresent = (matchingIndex >= 0);
            friend_feed = (matchingIndex >= 0) ? friendFeeds.splice(matchingIndex, 1)[0] : friendFeeds.shift();
        } else {
            _filterFriendsViralPresent = true;
            friend_feed = FeedStorageData.localStorage.feeds.friends.shift();
        }

        //let public_feed = FeedStorageData.localStorage.feeds.public.shift();
        //let friend_feed = FeedStorageData.localStorage.feeds.friends.shift();
        if (public_feed) {
            FeedStorageData.shown.public.unshift(public_feed);
            // Pull of old feeds so that the list doesn't bloat infinitely
            while (FeedStorageData.shown.public.length > FeedConstants.FEED_SHOWN_LIST_MAX_LEN) {
                FeedStorageData.shown.public.pop();
            }
        }

        if (friend_feed) {
            FeedStorageData.shown.friends.unshift(friend_feed);
            // Pull of old feeds so that the list doesn't bloat infinitely
            while (FeedStorageData.shown.friends.length > FeedConstants.FEED_SHOWN_LIST_MAX_LEN) {
                FeedStorageData.shown.friends.pop();
            }
        }

        // Sync with component
        _sync();
        // sync back on LS
        Farm2FeedLocalStorage.Set({
            data: FeedStorageData.localStorage,
            key: _gameKey
        })
    }

    function ClaimedUpdate(chnl, feedId, status, msg) {
        var i = 0;
        ServiceLocator.ZTrack.Cast({
            counter: "click",
            kingdom: "right-rail",
            phylum: "claim",
            zClass: chnl,    // Could be either zynga, zfriend
            family: (_filterStrings.length > 0) ? "filter_on": "filter_off",
            genus: "attempt"
        });
        if (chnl === "zynga") {
            for (i = 0; i < FeedStorageData.shown.public.length; i++) {
                if (FeedStorageData.shown.public[i].id === feedId) {
                    FeedStorageData.shown.public[i].claim_data = {
                        current_state: status,
                        status_text: msg
                    }
                    return;
                }
            }
        } else {
            for (i = 0; i < FeedStorageData.shown.friends.length; i++) {
                if (FeedStorageData.shown.friends[i].id === feedId) {
                    FeedStorageData.shown.friends[i].claim_data = {
                        current_state: status,
                        status_text: msg
                    }
                    return;
                }
            }
        }
    }

    function OnPublicCheck(data) {
        ZLogger.log("OnPublicCheck: got data: ", data);
        if (data && data.e === 0) {
            ZLogger.log("updating users..");
            var users = data.aux;
            if (users) {
                // Store user profile data for future usage
                for (var k in users) {
                    FeedStorageData.localStorage.users_profile_public[k] = users[k];
                }
            }
            ZLogger.log("updating feeds cache");
            var feeds = data.feeds;
            for (var i = 0; i < feeds.length; i++) {
                var feed = feeds[i];
                // Check if this feed is already registered with our FSD
                if (!FeedStorageData.unique.public[feed.id]) {
                    // If not, then register it, push to the appropriate domain
                    // and add the user contribution for future user tracking and cleanup.
                    // Also since this feed is from public, add a public chnl attribute
                    feed.chnl = "zynga";
                    FeedStorageData.unique.public[feed.id] = true;
                    FeedStorageData.localStorage.feeds.public.push(feed);
                    FeedStorageData.user_accountibility.public[feed.azid] = (FeedStorageData.user_accountibility.public[feed.azid] ? FeedStorageData.user_accountibility.public[feed.azid] : 0) + 1;
                }
            }
            FeedStorageData.localStorage.lastTS = GenericUtils.TimeNowSeconds();
            // Flush back to disk
            Farm2FeedLocalStorage.Set({
                data: FeedStorageData.localStorage,
                key: _gameKey
            })
            // Fire is called post loading feed data to localstorage
            ServiceLocator.Events.Queue.Fire(ServiceLocator.Events.Queue.Contexts.FetchPublicFeed)
        }
    }

    function OnFriendCheck(data) {
        ZLogger.log("OnFriendCheck: got data: ", data);
        if (data && data.e === 0) {
            ZLogger.log("updating feeds cache");
            var feeds = data.feeds;
            for (var i in feeds) {
                var feed = feeds[i];
                if (!FeedStorageData.unique.friends[feed.id]) {
                    // Since this feed is from a friend, add a zfriend chnl attribute
                    feed.chnl = "zfriend";
                    FeedStorageData.unique.friends[feed.id] = true;
                    FeedStorageData.localStorage.feeds.friends.push(feed);
                    FeedStorageData.user_accountibility.friends[feed.azid] = (FeedStorageData.user_accountibility.friends[feed.azid] ? FeedStorageData.user_accountibility.friends[feed.azid] : 0) + 1;
                    _lastFriendFeedCheckedTs = Math.max(_lastFriendFeedCheckedTs, parseInt(feed.ts));
                }
            }
            FeedStorageData.localStorage.lastTS = GenericUtils.TimeNowSeconds();
            // Flush back to disk
            Farm2FeedLocalStorage.Set({
                data: FeedStorageData.localStorage,
                key: _gameKey
            })
        }
    }

    function OnCheckError(err) {
        ZLogger.fatal("Error encountered on feed refresh:", err);
    }

    function InitializeFromObject() {
        var data = GetFromLocalStorage();
        FeedStorageData.localStorage = FeedConstants.LOCAL_STORAGE_FEED_TEMPLATE;
        if (!data.needsRefresh) {
            FeedStorageData.localStorage = data;
            // rebuild the storage data from this
            var i, each_feed;
            for (i = 0; i < FeedStorageData.localStorage.feeds.public.length; ++i) {
                try {
                    each_feed = FeedStorageData.localStorage.feeds.public[i];
                    FeedStorageData.unique.public[each_feed.id] = true;
                    FeedStorageData.user_accountibility.public[each_feed.azid] = (FeedStorageData.user_accountibility.public[each_feed.azid] ? FeedStorageData.user_accountibility.public[each_feed.azid] : 0) + 1;
                } catch (e) {
                    ZLogger.error("Error when parsing public feed (skipping): ", e, each_feed);
                }
            }
            for (i = 0; i < FeedStorageData.localStorage.feeds.friends.length; ++i) {
                try {
                    each_feed = FeedStorageData.localStorage.feeds.public[i];
                    FeedStorageData.unique.friends[each_feed.id] = true;
                    FeedStorageData.user_accountibility.friends[each_feed.azid] = (FeedStorageData.user_accountibility.friends[each_feed.azid] ? FeedStorageData.user_accountibility.friends[each_feed.azid] : 0) + 1;
                } catch (e) {
                    ZLogger.error("Error when parsing friend feed (skipping): ", e, each_feed);
                }
            }

        }
        // Removing prime feed call for player zid since it causes cannibalization of friends feeds.
        // InitializeChecks({ locale: _locale });
       startFeedHandlers();
        // if (data.needsRefresh) {
        //     // Since it needs a refresh immediately hence query right now as well
        //     Farm2FeedAPI.Check(_lastFriendFeedCheckedTs);
        // }
        // Also start trickling from now on and request for fresh feed data
        // Enqueues till the data is stored to localstorage
        ServiceLocator.Events.Queue.Wire(ServiceLocator.Events.Queue.Contexts.FetchPublicFeed, () => {
            Farm2FeedController.Trickle(true)
        });
        //Farm2FeedController.Trickle(true);
        Farm2FeedAPI.Check(_lastFriendFeedCheckedTs);
        _lastNormalRefreshTime = GenericUtils.TimeNowSeconds();
    }

    function startFeedHandlers() {
        intervalHandlers.trickle = setInterval(() => { Farm2FeedController.Trickle(); }, FeedConstants.TRICKLE_FEED_INTERVAL_SECONDS * 1000);
        intervalHandlers.check = setInterval(() => {
            Farm2FeedAPI.Check(_lastFriendFeedCheckedTs);
            _lastNormalRefreshTime = GenericUtils.TimeNowSeconds();
            }, FeedConstants.FEED_POLL_TIMER_SECONDS * 1000)
    }

    function GetFromLocalStorage() {
        var result = FeedConstants.LOCAL_STORAGE_FEED_TEMPLATE;
        if (Farm2FeedLocalStorage.Has(_gameKey)) {
            var storedData = Farm2FeedLocalStorage.Get(_gameKey);
            if (
                storedData.csrf !== _csrf ||
                storedData.lastTS + FeedConstants.LOCAL_STORAGE_EXPIRE_INTERVAL_SECONDS < GenericUtils.TimeNowSeconds()
            ) {
                // Looks like this data is stale and should be flushed
                result.needsRefresh = true;
            } else {
                // Seems like we can live with this data for some time
                result = storedData;
                result.needsRefresh = false;
            }
        } else {
            result.csrf = _csrf;
            result.needsRefresh = true;
        }

        return result;
    }

    function UserLookup(key) {
        var result = {};
        if (key) {
            if (key in FeedStorageData.localStorage.users_profile_public) {
                result = FeedStorageData.localStorage.users_profile_public[key];
            }
            if (ServiceLocator.Site.SocialExists(key)) {
                result = ServiceLocator.Site.SocialLookup(key);
            }
        }
        //ZLogger.log("Userlookup: ", key, " Got: ", result);
        return result;
    }

    /** Do any other clean up here before exiting */
    function Clear() {
        ZLogger.log("clearing the intervals: ")
        for (var eachInterval in intervalHandlers) {
            var intervalTimer = intervalHandlers[eachInterval];
            if (intervalTimer && window) {
                window.clearInterval(intervalTimer);
            }
        }
    }

    /** Instantiate with session params and required references */
    function Init({
        that, csrf, zid, zFriends = {}, gameKey = "", gameId = 118
    }) {
        // Only init it once in the lifecycle
        if (!this._initDone) {
            _csrf = csrf;
            _that = that;
            _zid = zid;
            _gameKey = gameKey;
            _locale = LocUtils.GetActiveLocaleStandardCode();
            ZLogger.log("=====>Init Controller Feed: got friends: ", zFriends);
            Farm2FeedAPI.Init({
                url_public: process.env.REACT_APP_AUTH_ISSUETOKEN + "/ajax/common_web:feed_publicGet",
                url_friends: process.env.REACT_APP_AUTH_ISSUETOKEN + "/ajax/common_web:feed_neighborGet",
                zFriends: zFriends,
                poc_public: OnPublicCheck,
                poc_friends: OnFriendCheck,
                onErr: OnCheckError,
                csrf: _csrf,
                zid: _zid,
                locale: _locale,
                max_feeds_public: 15,
                max_feeds_friends: 15,
                gameKey: _gameKey,
                gameId: gameId
            })
            FeedStorageData = FeedConstants.FEED_IN_MEMORY_TEMPLATE;
            InitializeFromObject();
            // this._initDone = true;
        }

        ServiceLocator.Auth.Game.FacebookGameRefreshSig(_csrf,
            (data) => {
                ZLogger.log("==== attempting Got: ", data);
            },
            (err) => {
                ZLogger.log("==== attempting FAILED: ", err);
            })
    }

    function SetFilterStrings(filterStrings, isExclusionSearch) {
        _filterStrings = filterStrings;
        _isExclusionSearch = isExclusionSearch;
    }

    return {
        CanShowFriends: CanShowFriends,
        Clear: Clear,
        Claim: FeedClaimHelper,
        ClaimedUpdate: ClaimedUpdate,
        FlipToggleFriends: FlipToggleFriends,
        Init: Init,
        OnCheckError: OnCheckError,
        OnFriendCheck: OnFriendCheck,
        OnPublicCheck: OnPublicCheck,
        Trickle: Trickle,
        UserLookup: UserLookup,
        SetFilterStrings: SetFilterStrings
    }
})();

export default Farm2FeedController;